import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { useSupabase } from '@/hooks/useSupabase';
import { supabase } from '@/lib/supabase';
import type {
  ContactChannel,
  ConversationStatus,
  LeadConversationsInsert,
  LeadConversationsRow,
  LeadConversationsUpdate,
  LeadMessagesInsert,
  LeadMessagesRow,
  ProspectNotesRow,
} from '@/types/database.types';

import {
  countByStatus,
  createConversation,
  createLeadNote,
  createMessage,
  getConversation,
  getOriginCampaignEmail,
  listConversations,
  listLeadNotes,
  listMessages,
  listUnreadConversations,
  markConversationRead,
  updateConversation,
  type LeadsFilters,
  type LeadsListResult,
  type OriginCampaignEmail,
  type UnreadConversation,
} from './api';
import { subscribeToLeads, type LeadsRealtimeEvent } from './realtime';

export const leadsKeys = {
  all: ['leads'] as const,
  lists: () => [...leadsKeys.all, 'list'] as const,
  list: (f: LeadsFilters, p: number) =>
    [...leadsKeys.lists(), f, p] as const,
  counts: () => [...leadsKeys.all, 'counts'] as const,
  detail: (id: string) => [...leadsKeys.all, 'detail', id] as const,
  messages: (id: string) => [...leadsKeys.all, 'messages', id] as const,
  notes: (id: string) => [...leadsKeys.all, 'notes', id] as const,
  unread: () => [...leadsKeys.all, 'unread'] as const,
};

export function useConversations(filters: LeadsFilters, page: number) {
  return useQuery<LeadsListResult>({
    queryKey: leadsKeys.list(filters, page),
    queryFn: () => listConversations(filters, page),
    staleTime: 15_000,
  });
}

export function useConversationsCounts() {
  return useQuery<Partial<Record<ConversationStatus, number>>>({
    queryKey: leadsKeys.counts(),
    queryFn: countByStatus,
    staleTime: 30_000,
  });
}

export function useConversation(id: string | undefined) {
  return useQuery<LeadConversationsRow | null>({
    queryKey: id ? leadsKeys.detail(id) : ['leads', 'detail', 'unset'],
    queryFn: () => getConversation(id as string),
    enabled: !!id,
    staleTime: 15_000,
  });
}

export function useMessages(conversationId: string | undefined) {
  return useQuery<LeadMessagesRow[]>({
    queryKey: conversationId
      ? leadsKeys.messages(conversationId)
      : ['leads', 'messages', 'unset'],
    queryFn: () => listMessages(conversationId as string),
    enabled: !!conversationId,
    staleTime: 10_000,
  });
}

export function useCreateConversation() {
  const qc = useQueryClient();
  const { user } = useSupabase();
  return useMutation<
    LeadConversationsRow,
    Error,
    Omit<LeadConversationsInsert, 'owner_user_id'>
  >({
    mutationFn: (input) => {
      if (!user) throw new Error('Sin sesión');
      return createConversation({ ...input, owner_user_id: user.id });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadsKeys.lists() });
      void qc.invalidateQueries({ queryKey: leadsKeys.counts() });
      toast.success('Conversación creada');
    },
    onError: (e) =>
      toast.error('No se pudo crear', { description: e.message }),
  });
}

export function useUpdateConversation() {
  const qc = useQueryClient();
  return useMutation<
    LeadConversationsRow,
    Error,
    { id: string; patch: LeadConversationsUpdate }
  >({
    mutationFn: ({ id, patch }) => updateConversation(id, patch),
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: leadsKeys.lists() });
      void qc.invalidateQueries({ queryKey: leadsKeys.counts() });
      void qc.invalidateQueries({ queryKey: leadsKeys.detail(row.id) });
    },
    onError: (e) =>
      toast.error('No se pudo actualizar', { description: e.message }),
  });
}

/** Mail de campaña que originó el lead — primer mensaje del timeline. */
export function useOriginCampaignEmail(conversationId: string | undefined) {
  return useQuery<OriginCampaignEmail | null>({
    queryKey: conversationId
      ? ([...leadsKeys.all, 'origin', conversationId] as const)
      : ['leads', 'origin', 'unset'],
    queryFn: () => getOriginCampaignEmail(conversationId as string),
    enabled: !!conversationId,
    staleTime: 5 * 60_000, // el mail de origen no cambia
  });
}

export function useLeadNotes(conversationId: string | undefined) {
  return useQuery<ProspectNotesRow[]>({
    queryKey: conversationId
      ? leadsKeys.notes(conversationId)
      : ['leads', 'notes', 'unset'],
    queryFn: () => listLeadNotes(conversationId as string),
    enabled: !!conversationId,
    staleTime: 15_000,
  });
}

export function useCreateLeadNote(conversationId: string, prospectId: string | undefined) {
  const qc = useQueryClient();
  const { user } = useSupabase();
  return useMutation<ProspectNotesRow, Error, string>({
    mutationFn: (body) => {
      if (!user) throw new Error('Sin sesión');
      if (!prospectId) throw new Error('Conversación sin prospecto');
      return createLeadNote({
        conversationId,
        prospectId,
        authorUserId: user.id,
        body,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadsKeys.notes(conversationId) });
      toast.success('Nota guardada');
    },
    onError: (e) =>
      toast.error('No se pudo guardar la nota', { description: e.message }),
  });
}

/** Conversaciones con respuestas sin leer (badge sidebar, lista y campanita). */
export function useLeadsUnread() {
  return useQuery<UnreadConversation[]>({
    queryKey: leadsKeys.unread(),
    queryFn: listUnreadConversations,
    staleTime: 15_000,
  });
}

export function useMarkConversationRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => markConversationRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadsKeys.unread() });
    },
    // sin toast: es una acción implícita al abrir la conversación
  });
}

/**
 * Suscripción realtime → invalida las queries de leads según el evento.
 * Montarlo UNA vez (layout); los componentes consumen las queries normales.
 * El drafts key vive en drafts/hooks.ts pero se replica acá para invalidar
 * sin import circular.
 */
export function useLeadsRealtimeInvalidator(): void {
  const qc = useQueryClient();
  const { session } = useSupabase();
  useEffect(() => {
    if (!session?.user?.id) return;
    const handle = (event: LeadsRealtimeEvent) => {
      switch (event.kind) {
        case 'conversation':
          void qc.invalidateQueries({ queryKey: leadsKeys.lists() });
          void qc.invalidateQueries({ queryKey: leadsKeys.counts() });
          void qc.invalidateQueries({ queryKey: leadsKeys.unread() });
          if (event.conversationId) {
            void qc.invalidateQueries({
              queryKey: leadsKeys.detail(event.conversationId),
            });
          }
          break;
        case 'message':
          if (event.conversationId) {
            void qc.invalidateQueries({
              queryKey: leadsKeys.messages(event.conversationId),
            });
            void qc.invalidateQueries({
              queryKey: leadsKeys.detail(event.conversationId),
            });
          }
          void qc.invalidateQueries({ queryKey: leadsKeys.lists() });
          void qc.invalidateQueries({ queryKey: leadsKeys.unread() });
          break;
        case 'draft':
          if (event.conversationId) {
            void qc.invalidateQueries({
              queryKey: [...leadsKeys.all, 'drafts', event.conversationId],
            });
          }
          break;
      }
    };
    const channel = subscribeToLeads(handle);
    return () => {
      void channel.unsubscribe();
    };
  }, [qc, session?.user?.id]);
}

export function useCreateMessage(conversationId: string) {
  const qc = useQueryClient();
  return useMutation<
    LeadMessagesRow,
    Error,
    Omit<LeadMessagesInsert, 'conversation_id'>
  >({
    mutationFn: (input) =>
      createMessage({ ...input, conversation_id: conversationId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: leadsKeys.messages(conversationId) });
      void qc.invalidateQueries({ queryKey: leadsKeys.detail(conversationId) });
      void qc.invalidateQueries({ queryKey: leadsKeys.lists() });
      void qc.invalidateQueries({ queryKey: leadsKeys.counts() });
    },
    onError: (e) =>
      toast.error('No se pudo guardar', { description: e.message }),
  });
}

/**
 * El handle del prospecto en el canal de la conversación.
 *
 * Se usa para «Copiar y abrir» en los leads sociales. Devuelve `null` cuando el
 * prospecto no tiene contacto cargado en esa red: ahí la acción no se ofrece,
 * pero el texto igual se copia.
 */
export function useProspectSocialHandle(
  prospectId: string | undefined,
  // `conversation_channel` y `contact_channel` comparten los nombres de los 5
  // canales pero son tipos distintos: el puente es el nombre.
  channel: ContactChannel | undefined,
) {
  return useQuery<string | null>({
    queryKey: ['prospect-social-handle', prospectId ?? '-', channel ?? '-'],
    queryFn: async () => {
      if (!prospectId || !channel) return null;
      const { data, error } = await supabase
        .from('prospect_contacts')
        .select('value')
        .eq('prospect_id', prospectId)
        .eq('channel', channel)
        .order('is_primary', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data?.value as string | undefined) ?? null;
    },
    enabled: !!prospectId && !!channel,
    staleTime: 5 * 60_000,
  });
}
